/** Speaker tab — the name card: how big, how long, how much of a role line,
 *  which corner, which face. */
import type { StudioInspectorProps } from "../../StudioInspector.tsx";
import { fieldLabelStyle } from "../../styles.ts";
import { CornerPicker, FontRow, SegmentedRow, SliderRow, cardPctToCorner, cornerToCardPct } from "../essentials/index.ts";

/** Role-line word counts the segments offer; anything else (a study, a chat
 *  turn) shows as its own read-only segment rather than silently snapping. */
const ROLE_PRESETS = [0, 2, 4, 6];

export function SpeakerTab({ inspector }: { inspector: StudioInspectorProps }): React.JSX.Element {
  const { layout, brand, onPreview, onCommit } = inspector;
  const card = layout.speakerCard;
  const words = card.descriptionWords;
  return (
    <>
      {/* `?? 230` / `?? 5`: the card's typography and timing are baked by the
          still generator, so an absent value means ITS default — the slider
          shows that default rather than snapping the card to a zero. */}
      <SliderRow
        label="Name size" value={card.nameFontPx ?? 230} min={60} max={320} step={5} unit="px"
        toPatch={(v) => ({ layout: { speakerCard: { nameFontPx: v } } })} commitLabel="Speaker name size"
        onPreview={onPreview} onCommit={onCommit}
      />
      <SliderRow
        label="Shows for" value={card.durationSec ?? 5} min={1} max={15} step={0.5} unit="s"
        toPatch={(v) => ({ layout: { speakerCard: { durationSec: v } } })} commitLabel="Speaker card duration"
        onPreview={onPreview} onCommit={onCommit}
      />
      <SegmentedRow
        label="Role line"
        value={words == null ? "auto" : ROLE_PRESETS.includes(words) ? String(words) : "custom"}
        options={[
          { id: "auto", label: "Auto" },
          { id: "0", label: "None" },
          { id: "2", label: "2" },
          { id: "4", label: "4" },
          { id: "6", label: "6" },
          ...(words != null && !ROLE_PRESETS.includes(words) ? [{ id: "custom", label: String(words) }] : []),
        ]}
        // null, not undefined: the UNSET signal has to survive JSON.
        toPatch={(v) => ({ layout: { speakerCard: { descriptionWords: v === "auto" ? null : Number(v) } } })}
        commitLabel="Role line"
        onPreview={onPreview} onCommit={onCommit}
      />
      <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
        <span style={{ ...fieldLabelStyle, marginBottom: 0 }}>Corner</span>
        <CornerPicker
          value={cardPctToCorner(card.xPct, card.yPct)}
          onPick={(c) => onCommit({ layout: { speakerCard: cornerToCardPct(c) } }, "Moved speaker card")}
        />
      </div>
      <FontRow
        label="Card font" value={brand.speakerFont} families={inspector.fontFamilies}
        sameAsLabel={brand.speakerFont === brand.captionFont ? "Same face as your captions." : undefined}
        onPick={(f) => onCommit({ brand: { speakerFont: f } }, `Speaker font → ${f}`)}
      />
    </>
  );
}
